/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { createPersistentItem, createShortUid, logDebug, logWarn } from '@amzn/vinyl-util'
import { type DeviceInfo, deviceInfoValidator } from '@amzn/vinyl-amzn'
import { deviceConfigs, getDeviceConfig } from '@/device/deviceConfig'
import { localSignInPersistence } from '@/auth/signInPersistence'
import { ctx } from '@/util/context'

const { target, errorHandler } = ctx('device')

const localDeviceInfo = createPersistentItem<DeviceInfo | null>({
    defaultValue: null,
    key: 'deviceInfo_v1',
    validator: deviceInfoValidator.orNull().assert,
})

let deviceInfo: DeviceInfo

export async function initializeDeviceInfo(): Promise<void> {
    let dI = await localDeviceInfo.get()
    if (!dI || dI.deviceType !== getDeviceConfig().deviceTypeId) {
        if (dI && isWebPlayer(dI.deviceType) !== isWebPlayer(getDeviceConfig().deviceTypeId)) {
            // The device type has changed and has not changed between web player and web player mobile, sign out.
            logWarn(target, 'deviceType changed from persistence')
            localSignInPersistence.remove().catch(errorHandler)
        }
        dI = createDeviceInfo()
        await localDeviceInfo.set(dI)
    }
    deviceInfo = dI
    logDebug(target, 'initializeDeviceInfo', dI)
}

/**
 * Returns true if device type is web player or web player mobile.
 */
function isWebPlayer(deviceTypeId: string): boolean {
    return (
        deviceTypeId === deviceConfigs.WebPlayer.deviceTypeId ||
        deviceTypeId === deviceConfigs.WebPlayerMobile.deviceTypeId
    )
}

export function getDeviceInfo(): DeviceInfo {
    return deviceInfo
}

export function createDeviceInfo(): DeviceInfo {
    const deviceId = createShortUid()
    return {
        deviceId,
        deviceName: `VinylAmznTestDevice_${deviceId}`,
        deviceType: getDeviceConfig().deviceTypeId,
    }
}
